import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
import { useLocation, /* useMatch, */ useNavigate } from 'react-router-dom';
import { /* COMPANY_SETTINGS_ROUTE, */ SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContextValue, CompanyClubState } from '@contexts/companyClub/companyClub.type';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { Club } from '@domain/club';
import { Company } from '@domain/company';
import { useGetCompanyClub } from '@hooks/company-club/use-get-company-club';

import { companyClubEmptyState } from './utils';

export const CompanyClubContext = createContext<CompanyClubContextValue>({
  ...companyClubEmptyState,
  setCompanyClubState: () => {},
  setCompanyState: () => {},
  setClubState: () => {},
  setEmptyCompanyClubState: () => {},
  isTestVenueCompany: false,
  isExternalPlatformEnabledForCompany: false,
  isVideoUploadEnabledForCompany: false,
  isLoadingCompanyClub: false,
});

export const CompanyClubProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setCompanyAuthId, setCompanyClubId } = useContext(CompanyAuthContext);
  const { isSelectVenueActive, testCompanyIds, externalPlatformEnabledIds, videoUploadEnabledCompanyIds } =
    useContext(FeatureFlagContext);
  const { isAuthenticated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const companyId = pathname.split('/')[1];
  const clubSlug = pathname.split('/')[2];

  const isTestVenueCompany = (testCompanyIds as string).includes(companyId);
  const isExternalPlatformEnabledForCompany = (externalPlatformEnabledIds as string).includes(companyId);
  const isVideoUploadEnabledForCompany = (videoUploadEnabledCompanyIds as string).includes(companyId);
  // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
  // const isRouteDisabled = useMatch(COMPANY_SETTINGS_ROUTE);
  const isCompanyClubContextEnabled = !!companyId && !companyId.includes(':') && !!clubSlug && !clubSlug.includes(':');

  const [companyClubState, setCompanyClubState] = useState<CompanyClubState>({
    id: '',
    legacyId: '',
    isImportLimited: false,
    isSiaeImportLimited: false,
  });

  const [companyState, setCompanyState] = useState<Company>({
    id: '',
    legacyId: '',
    name: '',
    commercialName: '',
    defaultChannel: {
      id: '',
      name: '',
      slug: '',
    },
  });

  const [clubState, setClubState] = useState<Club>({
    id: '',
    legacyId: '',
    coverUrl: '',
    logoUrl: '',
    name: '',
    slug: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
    city: {
      country: {
        id: '',
        name: '',
        isoCode: '',
        currency: '',
      },
      id: '',
      legacyId: '',
      coverUrl: '',
      name: '',
      slug: '',
      timezone: '',
    },
  });

  const setEmptyCompanyClubState = useCallback(() => {
    setClubState(companyClubEmptyState.clubState);
    setCompanyClubState(companyClubEmptyState.companyClubState);
    setCompanyState(companyClubEmptyState.companyState);
  }, []);

  const {
    data: companyClub,
    error: companyClubError,
    isLoading: isLoadingCompanyClub,
  } = useGetCompanyClub({
    companyId,
    clubSlug,
    options: {
      // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
      enabled: isCompanyClubContextEnabled && !!userState, // && !isRouteDisabled,
    },
  });

  const shouldNavigateToSelectVenue =
    userState &&
    !isCompanyClubContextEnabled &&
    pathname !== SELECT_VENUE_ROUTE &&
    pathname !== USER_SETTINGS_ROUTE &&
    isSelectVenueActive;

  useEffect(() => {
    if (shouldNavigateToSelectVenue) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [shouldNavigateToSelectVenue, navigate]);

  useEffect(() => {
    if (!!companyClubError && companyClubError.status === 403 && isSelectVenueActive && isAuthenticated) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [companyClubError, navigate, isSelectVenueActive, isAuthenticated]);

  useEffect(() => {
    if (companyClub && isCompanyClubContextEnabled && !companyClubError?.status) {
      const { id, legacyId, isImportLimited, isSiaeImportLimited } = companyClub;
      setCompanyClubState({
        id,
        legacyId,
        isImportLimited,
        isSiaeImportLimited,
      });
      if (companyClub?.club) setClubState(companyClub.club);
      if (companyClub?.company) setCompanyState(companyClub.company);
      setCompanyAuthId(companyClub.company.id);
      setCompanyClubId(companyClub.club.id);
    } else if (!isCompanyClubContextEnabled) {
      setEmptyCompanyClubState();
    }
  }, [
    companyId,
    clubSlug,
    companyClub,
    isCompanyClubContextEnabled,
    setEmptyCompanyClubState,
    userState?.isSuperAdmin,
    isSelectVenueActive,
    companyClubError,
    setCompanyAuthId,
    setCompanyClubId,
  ]);

  const memoizedContextValue = useMemo<CompanyClubContextValue>(
    () => ({
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isVideoUploadEnabledForCompany,
      isLoadingCompanyClub,
    }),
    [
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isVideoUploadEnabledForCompany,
      isLoadingCompanyClub,
    ]
  );

  return (
    <CompanyClubContext.Provider
      value={{
        ...memoizedContextValue,
      }}>
      {children}
    </CompanyClubContext.Provider>
  );
};
